import { Component, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';

import { Subscription } from 'rxjs';

import { TaxIdValidators } from '@unpispas/upp-defs';
import { CountryCodes } from '@unpispas/upp-defs';
import { geocodeService } from '@unpispas/upp-base';
import { languageService } from '@unpispas/upp-base';
import { languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { Client } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';

export interface UppClientChanged {
    /** Indica si el formulario de cliente permite continuar con el flujo de factura. */
    invoiceProceedable: boolean;
    /** Indica que el NIF/CIF actual no ha podido verificarse como válido. */
    taxIdUnverified: boolean;
}

@Component({
    selector: 'upp-client-edit',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-edit.html',
    styleUrls: [
        './upp-edit.scss'
    ],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]
})
export class UppClientEditComponent extends languageSupport implements OnChanges, OnDestroy {
    @Input() place: PlaceView | null = null;
    @Input() client: Client | null = null;
    @Input() readonly = false;
    @Input() accounts = true;
    @Input() lookup = true;

    @Output() Changed = new EventEmitter<UppClientChanged>();

    private _placeSubscription: Subscription | null = null;
    private _businessForm: FormGroup | null = null;
    private _ready = false;
    private _businessField: string | null = null;
    private _businessOptions: Client[] | null = null;
    private _lastAddress: string | null = null;
    private _validaddress = false;

    constructor(
        private lang: languageService,
        private change: ChangeDetectorRef,
        private geocode: geocodeService,
        public view: viewService
    ){
        super(lang, change);
    }

    ngOnChanges(changes: SimpleChanges): void {
        void changes;

        if (this._placeSubscription){
            this._placeSubscription.unsubscribe();
            this._placeSubscription = null;
        }

        if (this.place){
            this._placeSubscription = this.place.object.OnRefresh.subscribe(
            (targets: string[]) => {
                if (targets.includes('INVOICEBUSINESS')){
                    this.change.markForCheck();
                }
            });
        }

        this._businessForm = null;
        this.emitChange();
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._placeSubscription){
            this._placeSubscription.unsubscribe();
            this._placeSubscription = null;
        }
    }

    get ClientForm(): FormGroup {
        if (this._businessForm === null){
            this.loadClientForm();
        }
        return this._businessForm as FormGroup;
    }

    get Clients(): Client[] {
        if (!this.lookup){
            return [];
        }

        const _clients: Client[] = [];
        const _current = this.client;

        for (const _client of this.placeClients){
            if ((_client !== _current) && _client.IsValid && (this.accounts || !this.IsPayAccount(_client))){
                _clients.push(_client);
            }
        }

        return _clients;
    }

    get TaxIdUnverified(): boolean {
        if (this._businessForm === null){
            return false;
        }

        const _country = String(this.ClientForm.get('merchantCountry')?.value ?? this.client?.country ?? '').trim().toUpperCase();
        if (_country && !CountryCodes.Is_ES(_country)){
            return false;
        }

        const _taxid = TaxIdValidators.ToTaxId(String(this.ClientForm.get('merchantTaxid')?.value ?? '').trim());
        return !!(_taxid && !TaxIdValidators.ValidateTaxId(_taxid));
    }

    get IsSpain(): boolean {
        const _country = String(this.ClientForm.get('merchantCountry')?.value ?? this.client?.country ?? '').trim().toUpperCase() || null;
        return CountryCodes.Is_ES(_country);
    }

    get Country(): string {
        const _country = String(this.ClientForm.get('merchantCountry')?.value ?? this.client?.country ?? '').trim().toUpperCase() || null;
        return (_country && CountryCodes.Code2Name(_country)) || '';
    }

    get ValidAddress(): boolean {
        return this._validaddress;
    }

    IsPayAccount(client: Client): boolean {
        const _accounts = (this.place?.object as { accounts?: Array<{ client?: Client; IsValid?: boolean }> } | null)?.accounts;
        if (!Array.isArray(_accounts)){
            return false;
        }

        for (const _account of _accounts){
            if (_account?.IsValid && (_account.client === client)){
                return true;
            }
        }

        return false;
    }

    get BusinessOptions(): Client[] {
        if (!this._businessOptions){
            const _options: Client[] = [];
            const _name = String(this.ClientForm.get('merchantName')?.value || '').trim().toLowerCase();
            const _taxid = String(this.ClientForm.get('merchantTaxid')?.value || '').trim().toLowerCase();

            for (const _client of this.Clients){
                let _includes = !!_name || !!_taxid;

                if (_includes && _name && !_client.name?.toLowerCase().includes(_name)){
                    _includes = false;
                }

                if (_includes && _taxid && !_client.taxid?.toLowerCase().includes(_taxid)){
                    _includes = false;
                }

                if (_includes && (_options.length < 10)){
                    _options.push(_client);
                }
            }

            this._businessOptions = _options;
        }

        return this._businessOptions;
    }

    Autocomplete(field: string): boolean {
        if (this.readonly){
            return false;
        }

        if (!this._ready || (this._businessField !== field) || (this.BusinessOptions.length < 1)){
            return false;
        }

        if ((this.BusinessOptions.length === 1) && this.ClientForm.valid){
            return false;
        }

        return true;
    }

    AutocompleteOptions(field: string): void {
        this._businessOptions = null;
        this._businessField = field;
        this.OnValueChanged();
    }

    OnValueChanged(): void {
        this.emitChange();
    }

    OnAddressBlur(): void {
        void this.OnAddressChanged();
    }

    OnSelect(client: Client): void {
        this.ClientForm.patchValue({
            merchantName: client.name,
            merchantEmail: client.email,
            merchantAddress: client.address,
            merchantPostal: client.postal,
            merchantRegion: client.region,
            merchantTaxid: client.taxid,
            merchantCountry: client.country,
        });

        this._validaddress = !!String(client.country || '').trim();
        this.AddressValidators(client.country, String(client.address || '').trim().length !== 0);
        this.OnValueChanged();
    }

    async OnAddressChanged(): Promise<void> {
        const _formatted = String(this.ClientForm.get('merchantAddress')?.value || '').trim();
        if (_formatted === this._lastAddress){
            return;
        }

        this._lastAddress = _formatted;

        this.ClientForm.get('merchantPostal')?.setValue('');
        this.ClientForm.get('merchantRegion')?.setValue('');
        this.ClientForm.get('merchantCountry')?.setValue(null);
        this._validaddress = false;

        if (_formatted.length !== 0){
            const _address = await this.geocode.ResolveAddress(_formatted);
            let _postal = '';
            let _region = '';
            let _countryCode: string | null = null;

            if (_address){
                this._validaddress = true;
                _postal = String(_address['postal_code'] || '').trim();
                _region = String(_address['area']?.['level2'] || _address['area']?.['level1'] || '').trim();
                _countryCode = String(_address['area']?.['level3'] || '').trim().toUpperCase() || null;
            }

            this.ClientForm.get('merchantPostal')?.setValue(_postal);
            this.ClientForm.get('merchantRegion')?.setValue(_region);
            this.ClientForm.get('merchantCountry')?.setValue(_countryCode);

            this.AddressValidators(_countryCode, true);
        }
        else {
            this.ClientForm.get('merchantPostal')?.setValue('');
            this.ClientForm.get('merchantRegion')?.setValue('');
            this.ClientForm.get('merchantCountry')?.setValue(null);
            this._validaddress = false;

            this.AddressValidators(null, false);
        }

        this.OnValueChanged();
        this.change.markForCheck();
    }

    private syncClientFromForm(): void {
        if (!this.client){
            return;
        }

        this.client.name = String(this.ClientForm.get('merchantName')?.value || '');
        this.client.taxid = String(this.ClientForm.get('merchantTaxid')?.value ?? '');
        this.client.email = String(this.ClientForm.get('merchantEmail')?.value || '');
        this.client.address = String(this.ClientForm.get('merchantAddress')?.value || '');
        this.client.postal = String(this.ClientForm.get('merchantPostal')?.value || '');
        this.client.region = String(this.ClientForm.get('merchantRegion')?.value || '');
        this.client.country = String(this.ClientForm.get('merchantCountry')?.value || '').trim().toUpperCase() || null;
    }

    private get placeClients(): Client[] {
        const _clients = this.place?.object?.clients;
        return Array.isArray(_clients) ? _clients as Client[] : [];
    }

    private loadClientForm(): void {
        const _name = this.client?.name || '';
        const _taxid = this.client?.taxid || '';
        const _email = this.client?.email || '';
        const _address = this.client?.address || '';
        const _postal = this.client?.postal || '';
        const _region = this.client?.region || '';
        const _country = String(this.client?.country || '').trim().toUpperCase();

        if (this._businessForm === null){
            this._businessForm = new FormGroup({
                merchantName: new FormControl(_name, [Validators.required]),
                merchantEmail: new FormControl(_email, [Validators.email]),
                merchantAddress: new FormControl(_address),
                merchantPostal: new FormControl(_postal),
                merchantRegion: new FormControl(_region),
                merchantCountry: new FormControl(_country),
                merchantTaxid: new FormControl(_taxid, [
                    Validators.required
                ]),
            });
        }
        else {
            this._businessForm.patchValue({
                merchantName: _name,
                merchantEmail: _email,
                merchantAddress: _address,
                merchantPostal: _postal,
                merchantRegion: _region,
                merchantCountry: _country,
                merchantTaxid: _taxid,
            });
        }

        this.AddressValidators(_country, String(_address || '').trim().length !== 0);
        this._validaddress = !!_country;
                
        this._ready = false;
        this._businessForm.markAsPristine();
        setTimeout(() => {
            this._businessForm?.markAsPristine();
            this._ready = true;
        }, 0);

        this.change.markForCheck();
    }

    private invoiceProceedable(): boolean {
        const _form = this.ClientForm;
        const _taxErr = _form.get('merchantTaxid')?.errors;

        if (_form.get('merchantName')?.errors){
            return false;
        }
        if (_taxErr?.['required']){
            return false;
        }
        if (_form.get('merchantEmail')?.errors){
            return false;
        }
        if (_form.get('merchantPostal')?.errors){
            return false;
        }
        if (_form.get('merchantRegion')?.errors){
            return false;
        }

        return true;
    }

    private AddressValidators(countryCode: string | null | undefined, hasAddress: boolean): void {
        const _postal = this.ClientForm.get('merchantPostal');
        const _region = this.ClientForm.get('merchantRegion');
        const _country = String(countryCode || '').trim().toUpperCase() || null;
        const _addressRequired = hasAddress && (!_country || CountryCodes.Is_ES(_country));
        const _postalRequired = _addressRequired;
        const _regionRequired = _addressRequired;

        if (_postalRequired){
            _postal?.setValidators([Validators.required]);
        }
        else {
            _postal?.clearValidators();
        }

        if (_regionRequired){
            _region?.setValidators([Validators.required]);
        }
        else {
            _region?.clearValidators();
        }

        _postal?.updateValueAndValidity();
        _region?.updateValueAndValidity();
    }

    private emitChange(): void {
        if (!this._businessForm){
            this.loadClientForm();
        }

        this.syncClientFromForm();

        this.Changed.emit({
            invoiceProceedable: this.invoiceProceedable(),
            taxIdUnverified: this.TaxIdUnverified
        });
    }
}
